import { useMemo } from 'react';

import useAuth from '@/api/auth/useAuth';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useFeatureFlags } from '@/integrations/feature-flags/feature-flags';
import { useRoutePermissions } from '@/hooks/authz/useRoutePermissions';
// fallow-ignore-next-line circular-dependency
import { routeTree } from 'src/routes';

import type { AccessContext } from '@/routing/access-meta';

export function useAccessContext(): AccessContext {
  const { isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();

  const { permissions, isLoading: permissionsLoading } =
    useRoutePermissions(routeTree);

  // Every registered flag, so any `access.featureFlag` resolves by
  // construction — see `useFeatureFlags`.
  const featureFlags = useFeatureFlags();

  const userIsAdmin = useMemo(
    () => isAdmin(currentUser, viewMode),
    [currentUser, viewMode],
  );

  return {
    isAdmin: userIsAdmin,
    isFeatureEnabled: (flag) => featureFlags[flag],
    isModuleEnabled,
    permissions,
    permissionsLoading,
  };
}
